import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import { type Integration, IntegrationKey, IntegrationsApi } from 'entities/Integrations'
import type { IChatbotField } from 'entities/Chatbot'
import { getCustomTypeByData } from 'entities/MergeField/lib/getCustomTypeByData'
import { CustomField } from 'entities/CustomField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MergeField } from 'entities/MergeField/model/MergeField'
import { mockMergeField } from 'entities/MergeField/mock/MergeFieldMock'

export type ISelectedProperty = {
  id: string
  customField?: CustomField
  integration?: Integration
  label?: string
  default: boolean
  required?: boolean
  choices: string[]
}

export class CalendlyFieldsStore {
  private _fieldsMap: Map<string, IChatbotField> = new Map()
  private _selectedPropertiesMap: Map<string, ISelectedProperty> = new Map()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  private _fetchFields = async (bookAppointmentLinkId: string) => {
    try {
      this.dispose()

      const { data } = await IntegrationsApi.getCalendlyIntegrationMeetingsFields(
        IntegrationKey.calendly,
        { id: bookAppointmentLinkId }
      )

      const fields = data.additional_fields

      fields.forEach((item) => {
        const type = getCustomTypeByData(item.type)

        const chatbotField = {
          key: item.key,
          name: item.name,
          type: type,
          settings: {
            default: item.default,
            required: item.required,
            choices: item.choices,
          },
        }

        const customField = new CustomField({
          key: chatbotField.key,
          name: chatbotField.name,
          type: chatbotField.type,
          visible: true,
          editable: true,
          category_new: 'custom',
        })

        const integration = this._entities.integrationsStore.getIntegration(IntegrationKey.calendly)

        this._fieldsMap.set(item.key, chatbotField)

        if (integration) {
          this._selectedPropertiesMap.set(item.key, {
            id: chatbotField.key,
            customField: customField,
            integration: integration,
            label: chatbotField.name,
            default: chatbotField.settings?.default || false,
            required: chatbotField.settings?.required || false,
            choices: chatbotField.settings?.choices || [],
          })
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  fetchFields = new SingletonRequest(this._fetchFields).request

  dispose = () => {
    this._fieldsMap.clear()
    this._selectedPropertiesMap.clear()
  }

  get fields() {
    return Array.from(this._fieldsMap.values())
  }

  get selectedProperties() {
    return Array.from(this._selectedPropertiesMap.values())
  }

  get mergeFieldCalendly(): MergeField[] {
    return this.selectedProperties.reduce<MergeField[]>((state, item) => {
      const field = item.customField
      const integration = item.integration

      if (field && integration) {
        state.push(
          new MergeField({
            ...mockMergeField,
            integrationId: integration.id,
            integrationKey: integration.key,
            label: field.name,
            name: field.key,
            type: field.type,
            template: field.template,
          })
        )
      }

      return state
    }, [])
  }
}
